import React from 'react';
import { BdsButton, BdsIcon } from '@/core/constants/webComponents';
import './MetricsStaticsLinks.scss';

export interface StaticLinkItem {
  id: string;
  label: string;
  href?: string;
  icon?: string;
  isTitle?: boolean;
}

interface MetricsStaticLinksProps {
  items: StaticLinkItem[];
  onLinkClick?: (id: string, href?: string) => void;
}

interface LinkWrapperProps {
  item: StaticLinkItem;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

// Items without href (e.g. modal triggers) must not render an anchor, otherwise "open in new tab" leads to a 404.
const LinkWrapper: React.FC<LinkWrapperProps> = ({ item, onClick, children }) =>
  item.href ? (
    <a href={item.href} className="metrics-static-links__link" onClick={onClick}>
      {children}
    </a>
  ) : (
    <button type="button" className="metrics-static-links__link" onClick={onClick}>
      {children}
    </button>
  );

export const MetricsStaticLinks: React.FC<MetricsStaticLinksProps> = ({ 
  items,
  onLinkClick 
}) => {
  const handleItemClick = (e: React.MouseEvent, item: StaticLinkItem) => {
    if (item.isTitle) {
      e.preventDefault();
      return;
    }

    const eventTarget = e.currentTarget ?? document;
    eventTarget.dispatchEvent(
      new CustomEvent('mfe-metrics-static-link-click', {
        detail: { id: item.id, href: item.href },
        bubbles: true,
        composed: true,
      }),
    );

    if (onLinkClick) {
      e.preventDefault();
      onLinkClick(item.id, item.href);
    }
  };

  return (
    <nav className="metrics-static-links" aria-label="Navegação estática">
      <ul className="metrics-static-links__list">
        {items.map((item) => (
          <li 
            key={item.id} 
            className={`metrics-static-links__item ${item.isTitle ? 'metrics-static-links__item--title' : ''}`}
          >
            {item.isTitle ? (
              <div className="metrics-static-links__content">
                {item.icon && (
                  <BdsIcon 
                    name={item.icon} 
                  />
                )}
                <span>{item.label}</span>
              </div>
            ) : (
              <LinkWrapper item={item} onClick={(e) => handleItemClick(e, item)}>
                <BdsButton
                  variant="text"
                  color="content"
                  size="medium"
                  block
                  group-icon
                  justify-content="space-between"
                  icon-left={item.icon}
                  icon-theme="outline"
                >
                  {item.label}
                </BdsButton>
              </LinkWrapper>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};